import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useWorkspaces } from '../api/queries';
import type { AuthorizationDetails, SupabaseAuth } from '../auth/supabase';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { PageHeader } from '../ui/PageHeader';
import { Stack } from '../ui/Stack';
import { StatusState } from '../ui/StatusState';
import { useDocumentTitle } from '../useDocumentTitle';

type ConsentDetails = Extract<AuthorizationDetails, { kind: 'consent' }>;

export interface ConsentRouteProps {
  auth: SupabaseAuth;
  /** `authorization_id` from the URL; null when absent. */
  authorizationId: string | null;
  /** No Supabase session: the route remembers this URL and goes to /login. */
  onSignInNeeded(): void;
  /** Sends the browser to the agent (window.location.assign in the route). */
  redirect(url: string): void;
}

type State =
  | { kind: 'loading' }
  | { kind: 'consent'; details: ConsentDetails; host: string }
  | { kind: 'error'; title: string; message: string }
  | { kind: 'outage' }
  | { kind: 'deciding'; details: ConsentDetails; host: string };

/** The host of this authorization's redirect_uri (charter I4). Never a registered or self-reported URI. */
export function redirectHost(redirectUri: string): string {
  return new URL(redirectUri).host;
}

const START_AGAIN = 'Go back to your agent and start the connection again.';

const NO_HOST: State = {
  kind: 'error',
  title: "This agent's return address can't be shown, so it can't be approved",
  message: START_AGAIN
};

const EXPIRED: State = {
  kind: 'error',
  title: 'This request has expired or was already answered',
  message: START_AGAIN
};

const OUTAGE: State = { kind: 'outage' };

function WorkspaceList(): ReactElement | null {
  const workspaces = useWorkspaces();
  if (workspaces.isPending) return <p className="m-0 text-md text-muted">Loading your workspaces</p>;
  if (!workspaces.data) {
    return (
      <p className="m-0 text-md text-muted">Your workspaces could not be loaded. Approving still covers all of them.</p>
    );
  }
  return (
    <ul className="m-0 pl-4">
      {workspaces.data.map((workspace) => (
        <li key={workspace.id} className="text-md text-ink">
          {workspace.name}
        </li>
      ))}
    </ul>
  );
}

/** The consent screen an agent's authorization lands on: who is asking, what they get, approve or deny. */
export function ConsentRoute(props: ConsentRouteProps): ReactElement {
  useDocumentTitle('Connect an agent');
  const { auth, authorizationId, onSignInNeeded, redirect } = props;
  const [state, setState] = useState<State>(
    authorizationId === null
      ? { kind: 'error', title: 'This sign-in link is incomplete', message: START_AGAIN }
      : { kind: 'loading' }
  );
  const deciding = useRef(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (authorizationId === null) return;
    let cancelled = false;
    void (async () => {
      let token: string | null;
      try {
        token = await auth.getAccessToken();
      } catch {
        // A thrown lookup is an outage, not "signed out": bouncing to /login would loop while it lasts.
        if (!cancelled) setState(OUTAGE);
        return;
      }
      if (cancelled) return;
      if (token === null) {
        onSignInNeeded();
        return;
      }
      try {
        const details = await auth.getAuthorizationDetails(authorizationId);
        if (cancelled) return;
        if (details.kind === 'redirect') {
          redirect(details.redirectUrl);
          return;
        }
        // Fail closed: with no readable host there is nothing honest to show next to Approve.
        let host = '';
        try {
          host = redirectHost(details.redirectUri);
        } catch {
          host = '';
        }
        setState(host === '' ? NO_HOST : { kind: 'consent', details, host });
      } catch {
        if (!cancelled) setState(EXPIRED);
      }
    })();
    return () => {
      cancelled = true;
    };
    // The flow runs once per authorization; the callbacks are not part of its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, authorizationId, attempt]);

  async function decide(details: ConsentDetails, host: string, answer: 'approve' | 'deny'): Promise<void> {
    if (deciding.current) return;
    deciding.current = true;
    setState({ kind: 'deciding', details, host });
    try {
      const url =
        answer === 'approve'
          ? await auth.approveAuthorization(details.authorizationId)
          : await auth.denyAuthorization(details.authorizationId);
      // Stays in `deciding` on purpose: the browser leaves. It only lingers if a custom-scheme redirect does not leave the page.
      redirect(url);
    } catch {
      deciding.current = false;
      setState({
        kind: 'error',
        title: answer === 'approve' ? 'Approval failed' : 'Could not send your answer',
        message: START_AGAIN
      });
    }
  }

  let body: ReactElement;
  if (state.kind === 'loading') {
    body = <StatusState kind="loading" title="Loading this request" />;
  } else if (state.kind === 'outage') {
    body = (
      <StatusState
        kind="error"
        title="The sign-in service is not responding. Try again in a moment."
        action={
          <Button
            variant="secondary"
            onPress={() => {
              setState({ kind: 'loading' });
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </Button>
        }
      />
    );
  } else if (state.kind === 'error') {
    body = <StatusState kind="error" title={state.title} message={state.message} />;
  } else {
    const { details, host } = state;
    const busy = state.kind === 'deciding';
    body = (
      <Card>
        <Stack gap={4}>
          <Stack gap={2}>
            <div className="flex flex-row flex-wrap items-center gap-2">
              <strong className="text-lg text-ink">{details.clientName}</strong>
              <Badge tone="neutral">Self-reported name</Badge>
            </div>
            <p className="m-0 text-md text-muted">will send you back to {host}</p>
          </Stack>
          <p className="m-0 text-md text-ink">
            Act as you in all your workspaces: read and edit reports, upload data, update published links.
          </p>
          <Stack gap={2}>
            <p className="m-0 text-md font-semibold text-ink">Your workspaces</p>
            <WorkspaceList />
            <p className="m-0 text-md text-muted">Workspaces you create later are included.</p>
          </Stack>
          <div className="flex flex-row flex-wrap gap-2">
            <Button variant="primary" isDisabled={busy} onPress={() => void decide(details, host, 'approve')}>
              Approve
            </Button>
            <Button variant="secondary" isDisabled={busy} onPress={() => void decide(details, host, 'deny')}>
              Deny
            </Button>
          </div>
        </Stack>
      </Card>
    );
  }

  return (
    <main className="min-h-screen bg-desk font-ui p-6">
      <div className="max-w-lg mx-auto">
        <PageHeader title="Connect an agent" description="An agent is asking to connect to your ReportPad account." />
        {body}
      </div>
    </main>
  );
}
